import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, Users, MapPin, Plus, X, Truck, Sparkles, ArrowLeft } from "lucide-react";
import { publicApi } from "../lib/api";

/**
 * Customer-facing Special Events booking page.
 *
 * Group bookings for Weddings · Corporate · Prom · Funeral · Other where the
 * client needs multiple vehicles at the same event (e.g. Lux XL SUV +
 * Luxury SUV + Sprinter). One event → N linked bookings → one combined
 * confirmation.
 */
const EVENT_TYPES = [
  ["wedding", "Wedding"],
  ["corporate", "Corporate"],
  ["prom", "Prom"],
  ["funeral", "Funeral"],
  ["other", "Other"],
];

const VEHICLE_OPTIONS = ["Luxury Sedan", "Luxury SUV", "Lux XL SUV", "Sprinter Van", "Executive Coach"];

const blankVehicle = () => ({
  vehicle_name: "Luxury SUV",
  pickup_address: "",
  dropoff_address: "",
  pickup_datetime: "",
  pax: 4,
  luggage: 0,
  fare: 0,
});

export default function Events() {
  const [step, setStep] = useState(1); // 1=details, 2=vehicles, 3=confirm
  const [event, setEvent] = useState({
    event_type: "wedding",
    event_name: "",
    event_date: "",
    venue: "",
    notes: "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    poc_name: "",
    poc_phone: "",
  });
  const [vehicles, setVehicles] = useState([blankVehicle()]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const updateVehicle = (idx, patch) => {
    setVehicles(vs => vs.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  };
  const addVehicle = () => setVehicles(vs => [...vs, blankVehicle()]);
  const removeVehicle = (idx) => setVehicles(vs => vs.filter((_, i) => i !== idx));

  const totalFare = vehicles.reduce((s, v) => s + (Number(v.fare) || 0), 0);

  const submit = async () => {
    if (!event.event_name || !event.event_date || !event.customer_name) {
      toast.error("Event name, date, and client name are required");
      return;
    }
    if (vehicles.length === 0) {
      toast.error("At least one vehicle is required");
      return;
    }
    for (const [i, v] of vehicles.entries()) {
      if (!v.vehicle_name || !v.pickup_datetime || !v.pickup_address) {
        toast.error(`Vehicle ${i + 1}: name, pickup time, and pickup address are required`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const r = await publicApi.post("/events/group-book", {
        ...event,
        vehicles: vehicles.map(v => ({
          ...v,
          pax: Number(v.pax) || 1,
          luggage: Number(v.luggage) || 0,
          fare: Number(v.fare) || 0,
        })),
      });
      setResult(r.data);
      setStep(3);
      toast.success("Event booked — we'll be in touch shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-[#050505]">
      <header className="border-b border-black/10">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <Link to="/" data-testid="events-back-home"
                  className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#71717A] hover:text-[#050505] transition-colors">
              <ArrowLeft size={14}/> Home
            </Link>
            <Link to="/" data-testid="events-logo-home" className="block hover:opacity-80 transition-opacity">
              <div className="font-serif text-xl tracking-tight">DELUX LIMO</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#71717A] mt-0.5">Trans Group · Milwaukee</div>
            </Link>
          </div>
          <div className="text-xs uppercase tracking-[0.22em] text-[#71717A]">(414) 333-6275</div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-10" data-testid="events-page">
        <div className="mb-8">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#71717A] mb-3">
            <Sparkles size={12}/> Wedding &amp; Special Events
          </div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-none">Book multiple vehicles</h1>
          <p className="text-sm text-[#52525b] mt-3 max-w-lg">Same event, different vehicles — one confirmation. Perfect for weddings, corporate roadshows, proms, and family gatherings.</p>
        </div>

        {step === 3 && result ? (
          <div className="border border-[#050505]/10 p-8" data-testid="events-confirmation">
            <div className="overline mb-2 text-[#050505]">Event booked · {result.event_code}</div>
            <div className="font-serif text-3xl mb-4">{result.event_name}</div>
            <div className="text-sm text-[#52525b] mb-6">{result.event_date}{result.venue ? ` · ${result.venue}` : ""}</div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#71717A] mb-2">Reservations</div>
            <ul className="space-y-1 mb-6">
              {result.child_confirmation_codes.map(c => (
                <li key={c} className="font-mono text-sm text-[#050505]">#{c}</li>
              ))}
            </ul>
            <div className="flex justify-between items-baseline pt-4 border-t border-black/10">
              <span className="overline">Total</span>
              <span className="font-serif text-3xl" data-testid="events-total">${Number(result.total_fare).toFixed(2)}</span>
            </div>
            <p className="text-xs text-[#52525b] mt-4">A confirmation email has been sent. Dispatch will contact you within a few hours to finalise details.</p>
            <Link to="/" data-testid="events-confirm-home"
                  className="inline-flex items-center gap-1.5 mt-6 text-[10px] uppercase tracking-[0.22em] border border-black/20 px-4 py-2.5 hover:bg-[#050505] hover:text-white transition-colors">
              <ArrowLeft size={13}/> Back to home
            </Link>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Event details */}
            <section className="border border-black/10 p-6" data-testid="events-details-section">
              <div className="overline mb-4 text-[#050505]">Event details</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="field-label !text-[#71717A]">Event type</label>
                  <select className="field-input !border-black/15 !text-[#050505]"
                    data-testid="event-type"
                    value={event.event_type}
                    onChange={e => setEvent({ ...event, event_type: e.target.value })}
                  >
                    {EVENT_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label !text-[#71717A]">Event date</label>
                  <input type="date" className="field-input !border-black/15 !text-[#050505]"
                    data-testid="event-date" value={event.event_date}
                    onChange={e => setEvent({ ...event, event_date: e.target.value })}/>
                </div>
                <div className="md:col-span-2">
                  <label className="field-label !text-[#71717A]">Event name</label>
                  <input className="field-input !border-black/15 !text-[#050505]"
                    data-testid="event-name" placeholder="Jane &amp; John Wedding · ACME Q3 Kickoff · Riverside Prom"
                    value={event.event_name}
                    onChange={e => setEvent({ ...event, event_name: e.target.value })}/>
                </div>
                <div>
                  <label className="field-label !text-[#71717A]">Venue</label>
                  <input className="field-input !border-black/15 !text-[#050505]"
                    data-testid="event-venue" placeholder="Pfister Hotel"
                    value={event.venue}
                    onChange={e => setEvent({ ...event, venue: e.target.value })}/>
                </div>
                <div>
                  <label className="field-label !text-[#71717A]">Notes (optional)</label>
                  <input className="field-input !border-black/15 !text-[#050505]"
                    data-testid="event-notes" placeholder="Special requests"
                    value={event.notes}
                    onChange={e => setEvent({ ...event, notes: e.target.value })}/>
                </div>
              </div>
            </section>

            {/* Client + POC */}
            <section className="border border-black/10 p-6" data-testid="events-client-section">
              <div className="overline mb-4 text-[#050505]">Client + point of contact</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="field-label !text-[#71717A]">Client name</label>
                  <input className="field-input !border-black/15 !text-[#050505]"
                    data-testid="event-customer-name" value={event.customer_name}
                    onChange={e => setEvent({ ...event, customer_name: e.target.value })}/>
                </div>
                <div>
                  <label className="field-label !text-[#71717A]">Client email</label>
                  <input type="email" className="field-input !border-black/15 !text-[#050505]"
                    data-testid="event-customer-email" value={event.customer_email}
                    onChange={e => setEvent({ ...event, customer_email: e.target.value })}/>
                </div>
                <div>
                  <label className="field-label !text-[#71717A]">Client phone</label>
                  <input className="field-input !border-black/15 !text-[#050505]"
                    data-testid="event-customer-phone" value={event.customer_phone}
                    onChange={e => setEvent({ ...event, customer_phone: e.target.value })}/>
                </div>
                <div>
                  <label className="field-label !text-[#71717A]">POC name (planner / coordinator)</label>
                  <input className="field-input !border-black/15 !text-[#050505]"
                    data-testid="event-poc-name" value={event.poc_name}
                    onChange={e => setEvent({ ...event, poc_name: e.target.value })}/>
                </div>
                <div>
                  <label className="field-label !text-[#71717A]">POC phone</label>
                  <input className="field-input !border-black/15 !text-[#050505]"
                    data-testid="event-poc-phone" value={event.poc_phone}
                    onChange={e => setEvent({ ...event, poc_phone: e.target.value })}/>
                </div>
              </div>
            </section>

            {/* Vehicles */}
            <section className="border border-black/10 p-6" data-testid="events-vehicles-section">
              <div className="flex justify-between items-center mb-4">
                <div className="overline text-[#050505]">Vehicles · {vehicles.length}</div>
                <button
                  type="button"
                  data-testid="event-add-vehicle"
                  onClick={addVehicle}
                  className="text-[11px] uppercase tracking-[0.18em] text-[#050505] border-b border-[#050505] hover:opacity-70 inline-flex items-center gap-1"
                ><Plus size={12}/> Add another vehicle</button>
              </div>
              <div className="space-y-4">
                {vehicles.map((v, idx) => (
                  <div key={idx} className="border border-black/10 p-4 bg-black/[.02]" data-testid={`event-vehicle-row-${idx}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#71717A]">
                        <Truck size={12}/> Vehicle {idx + 1}
                      </div>
                      {vehicles.length > 1 && (
                        <button
                          type="button"
                          data-testid={`event-remove-vehicle-${idx}`}
                          onClick={() => removeVehicle(idx)}
                          className="text-[10px] uppercase tracking-[0.18em] text-red-500 hover:underline inline-flex items-center gap-1"
                        ><X size={11}/> Remove</button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="field-label !text-[#71717A]">Vehicle type</label>
                        <select className="field-input !border-black/15 !text-[#050505]"
                          data-testid={`event-vehicle-type-${idx}`}
                          value={v.vehicle_name}
                          onChange={e => updateVehicle(idx, { vehicle_name: e.target.value })}
                        >
                          {VEHICLE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="field-label !text-[#71717A]">Pickup date &amp; time</label>
                        <input type="datetime-local" className="field-input !border-black/15 !text-[#050505]"
                          data-testid={`event-vehicle-pickup-${idx}`}
                          value={v.pickup_datetime}
                          onChange={e => updateVehicle(idx, { pickup_datetime: e.target.value })}/>
                      </div>
                      <div className="md:col-span-2">
                        <label className="field-label !text-[#71717A]">Pickup address</label>
                        <input className="field-input !border-black/15 !text-[#050505]"
                          data-testid={`event-vehicle-pickup-addr-${idx}`}
                          value={v.pickup_address}
                          onChange={e => updateVehicle(idx, { pickup_address: e.target.value })}/>
                      </div>
                      <div className="md:col-span-2">
                        <label className="field-label !text-[#71717A]">Dropoff address</label>
                        <input className="field-input !border-black/15 !text-[#050505]"
                          data-testid={`event-vehicle-dropoff-addr-${idx}`}
                          value={v.dropoff_address}
                          onChange={e => updateVehicle(idx, { dropoff_address: e.target.value })}/>
                      </div>
                      <div>
                        <label className="field-label !text-[#71717A]"># of passengers</label>
                        <input type="number" min="1" className="field-input !border-black/15 !text-[#050505]"
                          data-testid={`event-vehicle-pax-${idx}`}
                          value={v.pax}
                          onChange={e => updateVehicle(idx, { pax: e.target.value })}/>
                      </div>
                      <div>
                        <label className="field-label !text-[#71717A]"># of bags</label>
                        <input type="number" min="0" className="field-input !border-black/15 !text-[#050505]"
                          data-testid={`event-vehicle-luggage-${idx}`}
                          value={v.luggage}
                          onChange={e => updateVehicle(idx, { luggage: e.target.value })}/>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <div className="flex flex-wrap items-center justify-between gap-4 border-t border-black/10 pt-6">
              <div>
                <div className="overline text-[#71717A]">{vehicles.length} vehicle{vehicles.length === 1 ? "" : "s"}</div>
                <div className="text-xs text-[#71717A] mt-1">Dispatch will confirm final pricing based on distance, hours, and any special requests.</div>
              </div>
              <button
                type="button"
                data-testid="event-submit"
                onClick={submit}
                disabled={submitting}
                className="btn-gold !bg-[#050505] !text-white !text-[11px] px-6 py-3"
              >{submitting ? "Booking…" : "Request event booking"}</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
